import React, { useRef } from 'react';
import InfoIcon from 'components/general/info_icon/info_icon';
import Tooltip from 'components/general/tooltip/tooltip';

const styles = require('./ai_assist.module.scss');

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt'];
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];

interface FileUploadSectionProps {
  selectedFile: File | null;
  isFileLoading: boolean;
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  isDisabled: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function validateFile(file: File): boolean {
  const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
  if (!ALLOWED_EXTENSIONS.includes(fileExtension) && !ALLOWED_MIME_TYPES.includes(file.type)) {
    window.Alerts.alert('Please upload a PDF, DOCX, or TXT file.');
    return false;
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    window.Alerts.alert('The file is too large. Maximum file size is 10MB.');
    return false;
  }

  return true;
}

function FileUploadIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9 16V10H5L12 3L19 10H15V16H9ZM5 20V18H19V20H5Z"
        fill="#343434"
      />
    </svg>
  );
}

function AttachFileIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16.5 6V17.5C16.5 19.71 14.71 21.5 12.5 21.5C10.29 21.5 8.5 19.71 8.5 17.5V5C8.5 3.62 9.62 2.5 11 2.5C12.38 2.5 13.5 3.62 13.5 5V15.5C13.5 16.05 13.05 16.5 12.5 16.5C11.95 16.5 11.5 16.05 11.5 15.5V6H10V15.5C10 16.88 11.12 18 12.5 18C13.88 18 15 16.88 15 15.5V5C15 2.79 13.21 1 11 1C8.79 1 7 2.79 7 5V17.5C7 20.54 9.46 23 12.5 23C15.54 23 18 20.54 18 17.5V6H16.5Z"
        fill="#A729D2"
      />
    </svg>
  );
}

function ReplaceIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6ZM18.76 7.74L17.3 9.2C17.75 10.03 18 10.99 18 12C18 15.31 15.31 18 12 18V15L8 19L12 23V20C16.42 20 20 16.42 20 12C20 10.43 19.54 8.97 18.76 7.74Z"
        fill="#343434"
      />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <svg className={styles.spinner} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#E5E5E5" strokeWidth="3" />
      <path
        d="M12 2C6.48 2 2 6.48 2 12"
        stroke="#A729D2"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function FileUploadSection({
  selectedFile,
  isFileLoading,
  onFileSelect,
  onFileRemove,
  isDisabled,
}: FileUploadSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (validateFile(file)) {
      onFileSelect(file);
    }
    event.target.value = '';
  };

  const handleDropzoneClick = () => {
    if (!isDisabled && !isFileLoading) {
      fileInputRef.current?.click();
    }
  };

  const handleReplaceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFileRemove();
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDisabled || isFileLoading) return;

    const file = e.dataTransfer.files?.[0];
    if (file && validateFile(file)) {
      onFileSelect(file);
    }
  };

  const renderEmptyState = () => (
    <div
      className={styles.fileDropzone}
      onClick={handleDropzoneClick}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleDropzoneClick()}
    >
      <FileUploadIcon />
      <div className={styles.fileDropzoneContent}>
        <p className={styles.fileDropzoneTitle}>Click here to upload a document</p>
        <p className={styles.fileDropzoneSubtitle}>
          Only &quot;.pdf&quot;, &quot;.docx&quot; and &quot;.txt&quot; files. Maximum file size of 10MB.
        </p>
      </div>
    </div>
  );

  const renderLoadingState = () => (
    <div className={styles.fileDropzone}>
      <LoadingSpinner />
      <div className={styles.fileDropzoneContent}>
        <p className={styles.fileDropzoneTitle}>Uploading your document...</p>
      </div>
    </div>
  );

  const renderFileReadyState = () => (
    <div className={styles.fileReady}>
      <AttachFileIcon />
      <div className={styles.fileReadyContent}>
        <p className={styles.fileReadyName}>{selectedFile?.name}</p>
        <p className={styles.fileReadySize}>{selectedFile ? formatFileSize(selectedFile.size) : ''}</p>
      </div>
      <button
        type="button"
        className={styles.fileReplaceButton}
        onClick={handleReplaceClick}
        aria-label="Replace file"
      >
        <ReplaceIcon />
      </button>
    </div>
  );

  return (
    <div className={styles.inputSection}>
      <div className={styles.inputHeader}>
        <div className={styles.labelRow}>
          <label className={styles.label} htmlFor="file-upload-input" id="file-upload-label">
            Upload a job description document
            <input
              id="file-upload-input"
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              onChange={handleFileChange}
              className={styles.fileInput}
              disabled={isDisabled}
              aria-labelledby="file-upload-label"
            />
          </label>
          <Tooltip text="Attach a file, and AI will extract the key details to prefill your job post.">
            <button type="button" className={styles.infoIcon} aria-label="Information">
              <InfoIcon width="18" height="18" />
            </button>
          </Tooltip>
        </div>
        <p className={styles.inputDescription}>
          Attach a file, and AI will extract the key details to prefill your job post.
        </p>
      </div>

      {isFileLoading && renderLoadingState()}
      {!isFileLoading && !selectedFile && renderEmptyState()}
      {!isFileLoading && selectedFile && renderFileReadyState()}
    </div>
  );
}
